import { zodResolver } from '@hookform/resolvers/zod'
import {
	AlertCircle,
	Bell,
	Calendar,
	DollarSign,
	Loader2,
	Sparkles,
	Store,
	X,
} from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import * as z from 'zod'

import { CategoryMultiSelect } from '@/components/category-multi-select'
import { FileUpload, type UploadedFile } from '@/components/file-upload'
import { LoadingButton } from '@/components/loading-button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
	type OcrLineItem,
	type OcrResult,
	useExtractReceipt,
} from '@/lib/extract-receipt'
import { isLimitReachedError } from '@/lib/limit-error'
import { trpc } from '@/trpc/client'

const CURRENCIES = [
	{ code: 'CZK', label: 'CZK — Czech koruna' },
	{ code: 'EUR', label: 'EUR — Euro' },
	{ code: 'USD', label: 'USD — US dollar' },
	{ code: 'GBP', label: 'GBP — British pound' },
	{ code: 'PLN', label: 'PLN — Polish złoty' },
	{ code: 'CHF', label: 'CHF — Swiss franc' },
	{ code: 'JPY', label: 'JPY — Japanese yen' },
	{ code: 'AUD', label: 'AUD — Australian dollar' },
	{ code: 'CAD', label: 'CAD — Canadian dollar' },
] as const

const NOTES_MAX = 2000

function formatLineItemsAsNotes(
	items: OcrLineItem[],
	currency: string | null,
): string {
	const lines = items.map((it) => {
		const qty =
			it.quantity != null && it.quantity !== 1 ? `${it.quantity}× ` : ''
		const total =
			it.total != null
				? ` — ${it.total.toFixed(2)}${currency ? ` ${currency}` : ''}`
				: ''
		return `- ${qty}${it.name}${total}`
	})
	const header = 'Items on receipt:'
	const body = [header, ...lines].join('\n')
	if (body.length <= NOTES_MAX) return body
	// Keep the earliest items — users scan receipts top-down, and the trailing
	// "- …" tells them the list was clipped.
	const ellipsis = '\n- …'
	let truncated = body
	while (truncated.length + ellipsis.length > NOTES_MAX && lines.length > 0) {
		lines.pop()
		truncated = [header, ...lines].join('\n')
	}
	return truncated + ellipsis
}

const formSchema = z.object({
	name: z.string().trim().min(1, 'Name is required').max(120),
	price: z
		.string()
		.refine((v) => v !== '' && !Number.isNaN(Number(v)), 'Enter a valid price')
		.refine((v) => Number(v) >= 0, 'Price must be zero or more'),
	currency: z.string().length(3).default('CZK'),
	purchaseDate: z.string().min(1, 'Purchase date is required'),
	warrantyMonths: z
		.string()
		.refine(
			(v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0),
			'Months must be a whole number',
		),
	notifyBeforeExpiry: z.boolean().default(false),
	notifyDaysBefore: z
		.string()
		.refine(
			(v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 1),
			'Lead time must be at least 1 day',
		),
	store: z.string().trim().max(120),
	notes: z.string().trim().max(2000),
	categoryIds: z.array(z.string()),
	file: z
		.object({
			fileKey: z.string(),
			fileMimeType: z.enum([
				'image/jpeg',
				'image/png',
				'image/webp',
				'application/pdf',
			]),
			fileSizeBytes: z.number(),
			fileOriginalName: z.string(),
		})
		.nullable(),
})

export type ItemFormValues = z.input<typeof formSchema>

export interface ItemFormSubmitValues {
	name: string
	price: number
	currency: string
	purchaseDate: Date
	warrantyMonths: number | null
	notifyBeforeExpiry: boolean
	notifyDaysBefore: number
	store: string | null
	notes: string | null
	categoryIds: string[]
	file: UploadedFile | null
}

interface ItemFormProps {
	defaultValues: Partial<ItemFormValues>
	submitLabel: string
	onSubmit: (values: ItemFormSubmitValues) => Promise<void>
	pending: boolean
	error: string | null
	cancelHref?: string
	onCancel?: () => void
	// When true, uploading a file triggers Claude OCR auto-extract and fills
	// any untouched form fields. Only set this on the create flow.
	autoFillOnUpload?: boolean
}

export function ItemForm({
	defaultValues,
	submitLabel,
	onSubmit,
	pending,
	error,
	onCancel,
	autoFillOnUpload = false,
}: ItemFormProps) {
	const navigate = useNavigate()
	const categoriesQuery = trpc.categories.list.useQuery()
	const [autoFillDismissed, setAutoFillDismissed] = useState(false)

	const form = useForm<ItemFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: '',
			price: '',
			currency: 'CZK',
			purchaseDate: new Date().toISOString().slice(0, 10),
			warrantyMonths: '',
			notifyBeforeExpiry: false,
			notifyDaysBefore: '14',
			store: '',
			notes: '',
			categoryIds: [],
			file: null,
			...defaultValues,
		},
	})

	// Pre-fill any form field the user hasn't touched yet. RHF's dirty flag
	// is enough — defaults like `currency: 'CZK'` or today's `purchaseDate`
	// are not dirty, so OCR is free to overwrite them with what the receipt
	// actually says.
	const applyExtraction = (r: OcrResult) => {
		const dirty = form.formState.dirtyFields
		const setIfNotDirty = (key: keyof ItemFormValues, value: string | null) => {
			if (value == null || value === '') return
			if (dirty[key as keyof typeof dirty]) return
			form.setValue(key, value as never, { shouldDirty: false })
		}
		setIfNotDirty('name', r.name)
		setIfNotDirty('price', r.price != null ? r.price.toFixed(2) : null)
		setIfNotDirty('currency', r.currency)
		setIfNotDirty('purchaseDate', r.purchaseDate)
		setIfNotDirty('store', r.store)
		// Skip the notes auto-fill for single-item receipts — the `name` field
		// above already covers it; listing one item in notes is just noise.
		if (r.lineItems.length >= 2) {
			setIfNotDirty('notes', formatLineItemsAsNotes(r.lineItems, r.currency))
		}
	}

	const extract = useExtractReceipt({
		onSuccess: (res) => {
			if (res.status === 'completed') {
				applyExtraction(res.result)
				setAutoFillDismissed(false)
			} else if (res.status === 'failed') {
				toast.error('Could not read the receipt — please fill in manually.')
			} else {
				// status === 'skipped' — OCR is unconfigured server-side. Tell the
				// user explicitly so the spinner-then-nothing isn't a mystery.
				toast('Receipt auto-fill is unavailable right now.')
			}
		},
		onError: (err) => {
			if (isLimitReachedError(err)) {
				toast.error(err.message, {
					action: {
						label: 'Upgrade',
						onClick: () => navigate('/app/settings/billing'),
					},
				})
				return
			}
			toast.error(err.message || 'Receipt auto-fill failed.')
		},
	})

	const notifyOn = form.watch('notifyBeforeExpiry') ?? false
	const warrantyMonths = form.watch('warrantyMonths') ?? ''
	const notifyEnabled =
		Boolean(notifyOn) && warrantyMonths !== '' && Number(warrantyMonths) > 0

	async function handleSubmit(values: ItemFormValues) {
		await onSubmit({
			name: values.name.trim(),
			price: Number(values.price),
			currency: values.currency || 'CZK',
			purchaseDate: new Date(values.purchaseDate),
			warrantyMonths:
				values.warrantyMonths === '' ? null : Number(values.warrantyMonths),
			notifyBeforeExpiry: notifyEnabled,
			notifyDaysBefore:
				values.notifyDaysBefore === '' ? 14 : Number(values.notifyDaysBefore),
			store: values.store.trim() === '' ? null : values.store.trim(),
			notes: values.notes.trim() === '' ? null : values.notes.trim(),
			categoryIds: values.categoryIds,
			file: values.file,
		})
	}

	return (
		<form
			onSubmit={form.handleSubmit(handleSubmit)}
			className="space-y-8"
			noValidate
		>
			{error ? (
				<Alert variant="destructive">
					<AlertCircle />
					<AlertTitle>Couldn't save</AlertTitle>
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			) : null}

			<section className="space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
						Receipt or invoice
					</h2>
				</div>
				<Controller
					control={form.control}
					name="file"
					render={({ field }) => (
						<FileUpload
							value={field.value as UploadedFile | null}
							onChange={(f) => field.onChange(f)}
							onSelected={
								autoFillOnUpload
									? (file, fileKey) => extract.mutate({ file, fileKey })
									: undefined
							}
							disabled={pending}
						/>
					)}
				/>
				{autoFillOnUpload && extract.isPending ? (
					<div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
						<Loader2 className="size-4 animate-spin text-primary" />
						<span>Reading your receipt…</span>
					</div>
				) : autoFillOnUpload &&
					extract.data?.status === 'completed' &&
					!autoFillDismissed ? (
					<div className="flex items-center justify-between gap-2 rounded-md border border-emerald-300/60 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
						<div className="flex items-center gap-2">
							<Sparkles className="size-4 text-emerald-700" />
							<span>
								Auto-filled from your receipt — please verify before saving.
							</span>
						</div>
						<button
							type="button"
							onClick={() => setAutoFillDismissed(true)}
							className="text-emerald-700 hover:text-emerald-900"
							aria-label="Dismiss auto-fill notice"
						>
							<X className="size-4" />
						</button>
					</div>
				) : null}
			</section>

			<section className="grid grid-cols-1 gap-5 md:grid-cols-2">
				<Field label="Name" error={form.formState.errors.name?.message}>
					<Input
						id="name"
						placeholder="MacBook Pro 16”"
						aria-invalid={!!form.formState.errors.name}
						{...form.register('name')}
					/>
				</Field>

				<Field
					label="Price"
					error={form.formState.errors.price?.message}
					icon={<DollarSign className="size-4" />}
				>
					<div className="flex gap-2">
						<Input
							className="flex-1"
							type="number"
							min={0}
							step="0.01"
							placeholder="0.00"
							aria-invalid={!!form.formState.errors.price}
							{...form.register('price')}
						/>
						<Controller
							control={form.control}
							name="currency"
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={field.onChange}
									disabled={pending}
								>
									<SelectTrigger
										className="w-28 shrink-0"
										aria-label="Currency"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{CURRENCIES.map((c) => (
											<SelectItem key={c.code} value={c.code}>
												{c.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</div>
				</Field>

				<Field
					label="Purchase date"
					error={form.formState.errors.purchaseDate?.message}
					icon={<Calendar className="size-4" />}
				>
					<Input
						type="date"
						aria-invalid={!!form.formState.errors.purchaseDate}
						{...form.register('purchaseDate')}
					/>
				</Field>

				<Field
					label="Warranty length (months)"
					error={form.formState.errors.warrantyMonths?.message}
					hint="Leave blank if no warranty"
				>
					<Input
						type="number"
						min={0}
						max={600}
						placeholder="24"
						aria-invalid={!!form.formState.errors.warrantyMonths}
						{...form.register('warrantyMonths')}
					/>
				</Field>

				<Field
					label="Store"
					error={form.formState.errors.store?.message}
					icon={<Store className="size-4" />}
				>
					<Input
						placeholder="Apple Store, Amazon, etc."
						{...form.register('store')}
					/>
				</Field>

				<div className="md:col-span-1">
					<Label className="text-sm font-medium">Categories</Label>
					<div className="mt-1.5">
						<Controller
							control={form.control}
							name="categoryIds"
							render={({ field }) => (
								<CategoryMultiSelect
									options={categoriesQuery.data ?? []}
									value={field.value}
									onChange={field.onChange}
									disabled={categoriesQuery.isPending || pending}
								/>
							)}
						/>
					</div>
				</div>

				<div className="md:col-span-2">
					<Field label="Notes" error={form.formState.errors.notes?.message}>
						<Textarea
							rows={3}
							placeholder="Anything worth remembering — serial numbers, accessories, etc."
							{...form.register('notes')}
						/>
					</Field>
				</div>
			</section>

			<section className="rounded-xl border bg-card p-5 space-y-3">
				<div className="flex items-start gap-3">
					<Controller
						control={form.control}
						name="notifyBeforeExpiry"
						render={({ field }) => (
							<Checkbox
								id="notify"
								checked={field.value}
								onCheckedChange={(v) => field.onChange(v === true)}
								disabled={warrantyMonths === '' || Number(warrantyMonths) === 0}
							/>
						)}
					/>
					<div className="-mt-0.5 flex-1 space-y-1">
						<Label
							htmlFor="notify"
							className="flex items-center gap-2 font-medium"
						>
							<Bell className="size-4 text-muted-foreground" />
							Notify me before warranty expires
						</Label>
						<p className="text-xs text-muted-foreground">
							{warrantyMonths === '' || Number(warrantyMonths) === 0
								? 'Add a warranty length to enable notifications.'
								: 'We’ll remind you so you have time to use it.'}
						</p>
					</div>
				</div>
				{notifyEnabled ? (
					<div className="ml-7 max-w-xs">
						<Label className="text-xs text-muted-foreground">
							Lead time (days)
						</Label>
						<Input
							className="mt-1.5"
							type="number"
							min={1}
							max={365}
							{...form.register('notifyDaysBefore')}
						/>
					</div>
				) : null}
			</section>

			<div className="flex items-center justify-end gap-2 border-t pt-6">
				{onCancel ? (
					<Button
						type="button"
						variant="ghost"
						onClick={onCancel}
						disabled={pending}
					>
						Cancel
					</Button>
				) : null}
				<LoadingButton type="submit" pending={pending}>
					{submitLabel}
				</LoadingButton>
			</div>
		</form>
	)
}

interface FieldProps {
	label: string
	error?: string
	hint?: string
	icon?: React.ReactNode
	children: React.ReactNode
}

function Field({ label, error, hint, children }: FieldProps) {
	return (
		<div>
			<Label className="text-sm font-medium">{label}</Label>
			<div className="mt-1.5">{children}</div>
			{error ? (
				<p className="mt-1 text-xs text-destructive">{error}</p>
			) : hint ? (
				<p className="mt-1 text-xs text-muted-foreground">{hint}</p>
			) : null}
		</div>
	)
}
